import { Box, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  useClearBotName,
  useSetBotName,
  useTenantConfig,
} from 'src/secret-menu/hooks/useTenantConfig';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  companyId: string;
};

export default function BotNameConfig({ companyId }: Props) {
  const { config } = useTenantConfig(companyId);
  const { setBotName, isLoading: isSaving } = useSetBotName(companyId);
  const { clearBotName, isLoading: isClearing } = useClearBotName(companyId);
  const [name, setName] = useState('');

  useEffect(() => {
    setName(config?.botName ?? '');
  }, [config?.botName]);

  const trimmedName = name.trim();
  const hasChange = trimmedName !== (config?.botName ?? '');

  const handleSave = async () => {
    try {
      await setBotName(trimmedName);
    } catch (error) {
      notifyAndLogError(
        'Failed to save bot name',
        'An error occurred while saving the bot name.',
        error,
      );
    }
  };

  const handleClear = async () => {
    try {
      await clearBotName();
    } catch (error) {
      notifyAndLogError(
        'Failed to clear bot name',
        'An error occurred while clearing the bot name.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      <Box maw={800}>
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
          Meeting bot name
        </Text>
        <Text size="textMd" fw={FONT_WEIGHTS.medium} mt="xxxs" c="gray.6">
          The name the bot joins meetings with. Leave blank to fall back to the tenant name.
        </Text>
      </Box>

      <Group align="flex-end" gap="sm">
        <TextInput
          label="Bot name"
          placeholder="Tenant name"
          value={name}
          onChange={event => setName(event.currentTarget.value)}
          maw={400}
          w="100%"
        />
        <Button
          onClick={handleSave}
          loading={isSaving}
          disabled={!trimmedName || !hasChange}
          leftSection={<IconDeviceFloppy size={16} />}
        >
          Save
        </Button>
        {config?.botName && (
          <Button
            variant="subtle"
            color="red"
            onClick={handleClear}
            loading={isClearing}
            leftSection={<IconTrash size={16} />}
          >
            Clear
          </Button>
        )}
      </Group>
    </Stack>
  );
}
